import { call, put, select, takeEvery } from "redux-saga/effects";

import {
  format,
  getFetchGetOptions,
  getLocaleWithScript,
  simpleAxiosUrlFetch,
} from "@gamedev-web/gamedev-sdk-core";
import { toError } from "@gaming/data-validation";
import { ERT } from "@gaming/extract-redux-saga-return-type";

import { extractUhfContentInTags } from "../helpers";
import { getUhfFromCache, storeUhfInCache } from "../helpers/uhfCache";
import { UhfData } from "../models";
import { UhfActions, uhfActions } from "./uhfActions";

const uhfUrlFormat =
  "https://uhf.microsoft.com/{0}/shell/xml/DEV_Games?headerId=DEV_GamesHeader&footerId=DEV_GamesFooter&targeting=DEV_Games&CookieComplianceEnabled=true";

function* fetchUhf() {
  const locale: string = yield select(getLocaleWithScript);

  let uhfData: UhfData | undefined = getUhfFromCache(locale);
  if (uhfData) {
    return uhfData;
  }

  const response: { data: string } | undefined = yield call(
    simpleAxiosUrlFetch,
    getFetchGetOptions(format(uhfUrlFormat, locale))
  );

  if (!response) {
    throw new Error("No response from uhf");
  }

  const rawData = response.data;
  const cssIncludes = extractUhfContentInTags(rawData, "cssIncludes");
  const javascriptIncludes = extractUhfContentInTags(rawData, "javascriptIncludes");
  const headerHtml = extractUhfContentInTags(rawData, "headerHtml");
  const footerHtml = extractUhfContentInTags(rawData, "footerHtml");

  uhfData = {
    cssIncludes,
    javascriptIncludes,
    headerHtml,
    footerHtml,
  };

  storeUhfInCache(uhfData, locale);

  return uhfData;
}

export function* fetchUhfSaga() {
  const startAction = uhfActions.fetchUhf.start();
  yield put(startAction);
  yield call(fetchUhfHandler, startAction);
}

function* fetchUhfHandler(_action: ReturnType<typeof uhfActions.fetchUhf.start>) {
  try {
    const data: ERT<typeof fetchUhf> = yield call(fetchUhf);
    if (data) {
      yield put(uhfActions.fetchUhf.success(data));
    } else {
      throw new Error("Failed to fetch metadata for UHF");
    }
  } catch (e) {
    const error = toError(e);
    console.error(error);
    yield put(uhfActions.fetchUhf.error(error));
  }
}

export function* uhfSagas() {
  yield takeEvery(UhfActions.FETCH_UHF_START, fetchUhfHandler);
}
